import { qtdMultiNoSite } from './qtdMultiSelecao';

/**
 * BUSCA DE /imoveis COM SELEÇÃO MÚLTIPLA + PISO DA RÉGUA DE PREÇO — por codsite.
 *
 * cs834 (Cecília Imóveis), pedido do Daniel em 20/10/2026, no visual do modelo #9 Atelier:
 *  - CONDOMÍNIOS e CATEGORIAS aceitam várias opções (cidades, bairros e tipos já aceitavam:
 *    cidades pelo modo multi-filiais, `useCidadesMulti`). Com 2+ cidades o select de BAIRROS
 *    passa a oferecer os bairros de todas elas.
 *  - a régua de preço de VENDA começa em R$ 500 mil. "Só a régua" (escolha do Daniel): sem o
 *    visitante mexer no preço a lista vem inteira; o piso só vale depois que ele mexe.
 *
 * Pedido de um cliente em código dos ~300 sites → gate por licença; fora do mapa nada muda.
 *
 * Contrato da URL (só com 2+ marcados; com 1 vale o de sempre — condomínio no caminho,
 * `?categoria=<id>`): `?condominios=<ids>` e `?categorias=<ids>`. Na busca, a lista vai nos
 * args `id_empreendimentos` / `id_categorias` do site-graphql (OU entre os ids).
 */
export const BUSCA_MULTI_CODSITES = new Set<number>([834]);

export const buscaMultiSelecao = (codsite: unknown): boolean => BUSCA_MULTI_CODSITES.has(Number(codsite));

export const PISO_PRECO_VENDA: Record<number, number> = {
    834: 500000,
};

// Piso da régua de venda; 0 = sem piso. Tenant cujo imóvel mais caro nem chega no piso fica sem
// ele (a régua viraria um ponto só).
export const pisoPrecoVenda = (codsite: unknown, tetoVenda?: unknown): number => {
    const piso = PISO_PRECO_VENDA[Number(codsite)] || 0;
    return piso > 0 && Number(tetoVenda) > piso ? piso : 0;
};

export const CONDOMINIOS_QUERY_PARAM = 'condominios';
export const CATEGORIAS_QUERY_PARAM = 'categorias';

const MAX_IDS_MULTI = 30;

// "12,7,12,abc" → [12, 7] (inteiros > 0, sem repetição, no máximo 30). Aceita o valor cru do
// router.query (string ou string[]).
export const parseIdsQuery = (value: unknown): number[] => {
    const raw = Array.isArray(value) ? value[0] : value;
    const vistos = new Set<number>();
    const ids: number[] = [];
    for (const parte of String(raw ?? '').split(',')) {
        const n = Number(String(parte).trim());
        if (!Number.isInteger(n) || n <= 0 || vistos.has(n)) continue;
        vistos.add(n);
        ids.push(n);
        if (ids.length >= MAX_IDS_MULTI) break;
    }
    return ids;
};

// A lista só manda quando tem 2+ ids — com um só, a busca segue pelo escalar de sempre.
export const idsMultiAtivos = (ids?: Array<number | string> | null): number[] | null => {
    const limpos = parseIdsQuery((Array.isArray(ids) ? ids : []).join(','));
    return limpos.length >= 2 ? limpos : null;
};

type BairroApi = { id?: any; nome?: any; id_cidade?: any };
type OpcaoCidade = { label?: string; value?: any };

// Bairros de VÁRIAS cidades num select só (respostas da query `bairros`, uma por cidade). Nome
// repetido entre cidades ("Centro") ganha a cidade entre parênteses — senão o visitante veria
// opções iguais. Mesmo formato do formatSelects (label/value), em ordem alfabética.
export const uniaoBairrosDasCidades = (
    respostas: Array<{ bairros?: BairroApi[] | null } | null | undefined>,
    cidades: OpcaoCidade[] | null | undefined
): Array<{ label: string; value: number; imoveis: number }> => {
    const nomeCidade = new Map<number, string>();
    (Array.isArray(cidades) ? cidades : []).forEach((c) => {
        if (Number(c?.value) > 0 && c?.label) nomeCidade.set(Number(c.value), String(c.label).trim());
    });
    const vistos = new Set<number>();
    const bairros: Array<{ nome: string; id: number; idCidade: number; imoveis: number }> = [];
    (Array.isArray(respostas) ? respostas : []).forEach((r) => {
        (Array.isArray(r?.bairros) ? r!.bairros! : []).forEach((b: any) => {
            const id = Number(b?.id);
            const nome = String(b?.nome ?? '').trim();
            if (!(id > 0) || !nome || vistos.has(id)) return;
            vistos.add(id);
            bairros.push({ nome, id, idCidade: Number(b?.id_cidade) || 0, imoveis: Number(b?.imoveis) || 0 });
        });
    });
    const chave = (nome: string) => nome.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const repetidos = new Map<string, number>();
    bairros.forEach((b) => repetidos.set(chave(b.nome), (repetidos.get(chave(b.nome)) || 0) + 1));
    return bairros
        .map((b) => {
            const cidade = nomeCidade.get(b.idCidade);
            const label = (repetidos.get(chave(b.nome)) || 0) > 1 && cidade ? `${b.nome} (${cidade})` : b.nome;
            return { label, value: b.id, imoveis: b.imoveis };
        })
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
};

// Acrescenta `?condominios=`/`?categorias=` (só os que têm 2+ ids) à URL da busca.
export const appendFiltrosMultiQuery = (
    path: string,
    condominios?: Array<number | string> | null,
    categorias?: Array<number | string> | null
): string => {
    let url = path;
    const pares: Array<[string, number[] | null]> = [
        [CONDOMINIOS_QUERY_PARAM, idsMultiAtivos(condominios)],
        [CATEGORIAS_QUERY_PARAM, idsMultiAtivos(categorias)],
    ];
    for (const [chave, ids] of pares) {
        if (!ids) continue;
        url = `${url}${url.includes('?') ? '&' : '?'}${chave}=${ids.join(',')}`;
    }
    return url;
};

// ---- MODELOS NOVOS: várias CIDADES, BAIRROS e TIPOS na home e na /imoveis ----

/**
 * Daniel 21/10/2026: "na busca da home opção de selecionar múltiplas cidades, bairros e tipo, isso
 * tem que aplicar para todos os modelos novos". Mesmo recorte do múltiplo de quartos
 * (`qtdMultiNoSite`: topo 10–14 ou busca 13–17) + o cs834. Na home, Cidade/Tipo (e o Bairro do
 * Atelier) marcam vários; na /imoveis o select de cidades vira o multi (antes só no modo
 * multi-filiais) e o de bairros junta os de todas as cidades marcadas. Fora daqui nada muda.
 *
 * Contrato da URL (o mesmo que a /imoveis já lê): tipos no caminho com `+`; 1 cidade no caminho;
 * 2+ cidades = caminho sem cidade + `?cidades=<ids>`; bairros no caminho com `+` (com 2+ cidades,
 * pelo rótulo da `uniaoBairrosDasCidades`).
 */
export const localMultiNoSite = (site: any): boolean => qtdMultiNoSite(site) || buscaMultiSelecao(site?.id);

/** Home: os ids marcados num texto só ("12,7"). "12,7,12, " → ['12', '7'] (na ordem em que foram marcados). */
export const idsDoTexto = (valor: unknown): string[] =>
    Array.from(new Set(String(valor ?? '').split(',').map((v) => v.trim()).filter(Boolean)));

/** Liga/desliga um id: ("12", "7") → "12,7"; ("12,7", "12") → "7". */
export const alternarIdTexto = (valor: unknown, id: unknown): string => {
    const alvo = String(id ?? '').trim();
    const atuais = idsDoTexto(valor);
    if (!alvo) return atuais.join(',');
    return (atuais.includes(alvo) ? atuais.filter((v) => v !== alvo) : [...atuais, alvo]).join(',');
};

/** As opções marcadas no texto, na ordem da marcação (id sem opção some). */
export const opcoesDoTexto = <T extends { value?: any }>(opcoes: T[] | null | undefined, valor: unknown): T[] => {
    const lista = Array.isArray(opcoes) ? opcoes : [];
    return idsDoTexto(valor)
        .map((id) => lista.find((o) => String(o?.value) === id))
        .filter((o): o is T => Boolean(o));
};

/** Resumo do campo: '' (nada), "Torres" (1) ou "3 cidades" (2+ — curto: nos campos estreitos o nome truncava e o
 *  "+2" sumia; os nomes ficam na lista marcada e no title). Sem `plural`, "Torres +2". */
export const resumoMarcados = (labels: string[], plural?: string): string =>
    labels.length === 0 ? '' : labels.length === 1 ? labels[0] : plural ? `${labels.length} ${plural}` : `${labels[0]} +${labels.length - 1}`;
